/* eslint-disable @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any --
   Validator/normalizer functions take unknown inputs. TypeScript requires 'as Record<string, X>'
   to index-access object properties after a typeof check — there is no assertion-free alternative. */
import { typeAssertExtends } from "./type_utils.ts";
import { type JSONObject } from "./json.ts";
import { validShelcode } from "./shelcode.ts";

export const attrNameRegex = /^[a-z_][a-z0-9_ ]*$/;

export interface ItemInfoNormalized {
  _id: string,
  name: string,
  shelcode: string | null,
  in: string | null,
  wherein: string | null,
  notes: string,
  deleted_at: string | null,
  attrs: { [key: string]: string },
  is_template?: boolean,
  template_id?: string | null | undefined,
  icon_id?: string | null | undefined,
  tags?: string[],
  scancodes?: Array<{code: string; type: string}>,
  qty?: number,
}

export interface ItemInfo {
  _id: string,
  _rev?: string,
  name: string,
  shelcode?: string | null | undefined,
  in?: string | null | undefined,
  wherein?: string | null | undefined,
  notes: string,
  deleted_at?: string | null | undefined,
  attrs?: { [key: string]: string },
  is_template?: boolean,
  template_id?: string | null | undefined,
  icon_id?: string | null | undefined,
  tags?: string[],
  scancodes?: Array<{code: string; type: string}>,
  qty?: number,
}

typeAssertExtends<ItemInfoNormalized, ItemInfo>();

const requiredStringAttrs = ["_id", "name", "notes"];
const optionalStringAttrs = ["shelcode", "in", "wherein", "deleted_at", "template_id", "icon_id"];

export function normalizeItemInfo<Content extends JSONObject>(val: Partial<ItemInfo> & Content): ItemInfoNormalized & Content {
  const res:Record<string, any> = {};
  for (const name in val) {
    let thisVal:any = val[name];
    if (optionalStringAttrs.includes(name)) {
      if (thisVal == null || thisVal === "")
        thisVal = null;
      else if (typeof thisVal !== "string")
        throw new TypeError(`val.${name} is not a string | null | undefined`);
    } else if (requiredStringAttrs.includes(name)) {
      if (typeof thisVal !== "string")
        throw new TypeError(`val.${name} is not a string`);
    } else {
      if (thisVal == null) {
        thisVal = null;
      } else if (!["boolean", "number", "bigint", "string"].includes(typeof thisVal)) {
        const jsonText = JSON.stringify(thisVal);
        if (typeof jsonText === "undefined")
          throw new TypeError(`val.${name} is not JSON.stringify-able`);
        thisVal = JSON.parse(jsonText);
      }
    }
    res[name] = thisVal;
  }
  for (const name of requiredStringAttrs) {
    if (!(name in res))
      throw new TypeError(`val.${name} missing`);
  }
  for (const name of optionalStringAttrs) {
    if (!(name in res))
      res[name] = null;
  }
  if ("attrs" in res) {
    const thisVal = res["attrs"];
    if (typeof thisVal !== "object" || thisVal == null)
      throw new TypeError(`val.attrs is not an object`);
    for (const attrName in thisVal) {
      if (typeof thisVal[attrName] !== "string")
        throw new TypeError(`val.attrs.${attrName} is not a string`);
      if (!attrNameRegex.test(attrName))
        throw new TypeError(`val.attrs.${attrName} is not a valid attr name`);
    }
  } else {
    res["attrs"] = {};
  }
  return res as ItemInfoNormalized & Content;
}

export function isItemInfo(val: unknown): val is ItemInfo {
  if (typeof val !== "object")
    return false;
  if (val == null)
    return false;
  const val2 = val as Record<string, any>;
  for (const name of requiredStringAttrs) {
    if (!(name in val2))
      return false;
    if (typeof val2[name] !== "string")
      return false;
  }
  for (const name of optionalStringAttrs) {
    if (!(name in val2))
      continue;
    if (val2[name] == null)
      continue;
    if (typeof val2[name] !== "string")
      return false;
  }
  if ("_rev" in val2 && typeof val2["_rev"] !== "string") return false;
  if ("is_template" in val2 && typeof val2["is_template"] !== "boolean") return false;
  if ("template_id" in val2 && val2["template_id"] != null && typeof val2["template_id"] !== "string") return false;
  if ("tags" in val2 && val2["tags"] != null) {
    if (!Array.isArray(val2["tags"])) return false;
    if (!(val2["tags"] as unknown[]).every(t => typeof t === "string")) return false;
  }
  if ("qty" in val2 && val2["qty"] != null && (typeof val2["qty"] !== "number" || !Number.isInteger(val2["qty"]) || (val2["qty"] as number) < 1)) return false;
  if ("scancodes" in val2 && val2["scancodes"] != null) {
    if (!Array.isArray(val2["scancodes"])) return false;
    if (!(val2["scancodes"] as unknown[]).every(s =>
      typeof s === "object" && s != null &&
      typeof (s as Record<string,unknown>)["code"] === "string" &&
      typeof (s as Record<string,unknown>)["type"] === "string"
    )) return false;
  }
  if ("shelcode" in val2) {
    if (val2["shelcode"] != null && typeof val2["shelcode"] !== "string") return false;
    if (val2["shelcode"] !== "" && val2["shelcode"] != null && !validShelcode(val2["shelcode"])) return false;
  }
  return true;
}

export function assertIsItemInfo(val: unknown): asserts val is ItemInfo {
  if (!isItemInfo(val)) {
    console.log("Value is ", val);
    throw new TypeError("Value is not a ItemInfo");
  }
}

export interface ItemAttachmentDoc {
  _id: string;
  _rev?: string;
  item_id: string;
  caption: string;
  deleted_at: string | null;
  primary?: boolean;
}

export function isItemAttachmentDoc(val: unknown): val is ItemAttachmentDoc {
  if (typeof val !== "object" || val == null) return false;
  const v = val as Record<string, unknown>;
  if (typeof v["_id"] !== "string" || !v["_id"].startsWith("item_attachment:")) return false;
  if (typeof v["item_id"] !== "string") return false;
  if (typeof v["caption"] !== "string") return false;
  if (v["deleted_at"] !== null && typeof v["deleted_at"] !== "string") return false;
  return true;
}

export function assertIsItemAttachmentDoc(val: unknown): asserts val is ItemAttachmentDoc {
  if (!isItemAttachmentDoc(val)) throw new TypeError("Value is not an ItemAttachmentDoc");
}

export interface ItemLinkDoc {
  _id: string;
  _rev?: string;
  deleted_at: string | null;
}

export function itemLinkId(idA: string, idB: string): string {
  const [a, b] = idA <= idB ? [idA, idB] : [idB, idA];
  return `item_link:${a}/${b}`;
}

export function itemLinkParts(linkId: string): [string, string] {
  const rest = linkId.slice("item_link:".length);
  const slash = rest.indexOf("/");
  return [rest.slice(0, slash), rest.slice(slash + 1)];
}

export function isItemLinkDoc(val: unknown): val is ItemLinkDoc {
  if (typeof val !== "object" || val == null) return false;
  const v = val as Record<string, unknown>;
  if (typeof v["_id"] !== "string") return false;
  const rest = (v["_id"] as string).slice("item_link:".length);
  const slash = rest.indexOf("/");
  if (slash === -1) return false;
  const a = rest.slice(0, slash);
  const b = rest.slice(slash + 1);
  if (!a.startsWith("item:") || !b.startsWith("item:")) return false;
  if (a >= b) return false;
  if (v["deleted_at"] !== null && typeof v["deleted_at"] !== "string") return false;
  return true;
}

export function assertIsItemLinkDoc(val: unknown): asserts val is ItemLinkDoc {
  if (!isItemLinkDoc(val)) throw new TypeError("Value is not an ItemLinkDoc");
}

export interface IconDoc {
  _id: string;
  _rev?: string;
  name: string;
  deleted_at: string | null;
}

export function isIconDoc(val: unknown): val is IconDoc {
  if (typeof val !== "object" || val == null) return false;
  const v = val as Record<string, unknown>;
  if (typeof v["_id"] !== "string" || !v["_id"].startsWith("icon:")) return false;
  if (typeof v["name"] !== "string") return false;
  if (v["deleted_at"] !== null && typeof v["deleted_at"] !== "string") return false;
  return true;
}

export function assertIsIconDoc(val: unknown): asserts val is IconDoc {
  if (!isIconDoc(val)) throw new TypeError("Value is not an IconDoc");
}
